import { type FC, memo, useCallback, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import {
  ButtonKind,
  ButtonShape,
  ButtonSize,
  ButtonVariant,
  Heading,
  IconButton,
  Tag,
  TagKind,
  TagVariant,
  Text,
} from "@stubhub/broadway-web";
import { MoreIcon } from "@stubhub/broadway-web/icons/functional";
import { useContent, useFormattedContent } from "src/shared/components/content";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { MobileBannerOverflowDrawer } from "src/website-apps/responsive/stubhub-performer/components/mobile-banner/MobileBannerOverflowDrawer";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import { scrollToPerformerCardAtom } from "src/website-apps/responsive/stubhub-performer/utils/scrollToPerformerCardAtom";
import { GRID_FILTER_TYPES } from "src/website-apps/shared/components/immersive-performer/eventlist-filters/constants";
import { HERO_BANNER_CONTENT_ID } from "src/website-apps/shared/constants/header";
import { useEventsGridContext } from "src/website-apps/shared/context/events-grid-context";
import { usePerformerInfoCardData } from "src/website-apps/shared/hooks/usePerformerInfoCardData";
import { getPerformerResidencyContext } from "src/website-apps/shared/utils/performerGridSurface";
import { titleSectionStyles } from "./styles";

/**
 * Compact header for the Residency mobile calendar surface.
 *
 * Displays performer image (left), name, Residency badge, and end date (right column).
 * MUST carry `id={HERO_BANNER_CONTENT_ID}` on the h1 to preserve page H1 and
 * scroll-anchor behavior when MobileBannerContent is suppressed (Task B1).
 */
export const TitleSection: FC = memo(() => {
  const storePageEvent = useStorePageEvent();
  const model = usePerformerPageModelData();
  const { categoryName, categoryTitle, imageUrl, categoryId } = model.categorySummary;
  const residencyContext = getPerformerResidencyContext(model.performerGridSurface);
  const endDate = residencyContext?.performerDateRange ?? "";
  const [state] = useEventsGridContext();
  const isParking = useMemo(() => state.filters.gridFilterType === GRID_FILTER_TYPES.PARKING, [state]);
  /** \{categoryName\} parking */
  const parkingTitle = useFormattedContent("cid$#3789327#", [categoryName ?? ""]);
  const displayTitle = isParking ? parkingTitle : (categoryTitle ?? categoryName);
  const residencyTagContent = useContent("cid$#4245661#"); // Residency
  const untilContent = useFormattedContent("cid$#4245664#", [endDate]); // Until {date}
  const iconButtonAriaLabelContent = useContent("cid$#4245670#"); // More actions menu
  const showBadge = residencyContext !== null;
  const residencyVenueSubtitle = residencyContext?.venueSubtitle;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const handleDrawerOpen = useCallback(() => {
    setIsDrawerOpen(true);
    storePageEvent(PE_TYPE.CLICK, PE_KEYS.PERFORMER_MOBILE_OVERFLOW_OPEN);
  }, [storePageEvent]);
  const handleDrawerClose = useCallback(() => setIsDrawerOpen(false), []);
  const { hasAnyData: catHasPerformerCard } = usePerformerInfoCardData();
  const { scrollToElement: scrollToPerformer } = useAtomValue(scrollToPerformerCardAtom);

  return (
    <div className={titleSectionStyles.container}>
      <div className={titleSectionStyles.innerRow}>
        {!!imageUrl && <img src={imageUrl} alt="" aria-hidden="true" className={titleSectionStyles.image} />}
        <div className={titleSectionStyles.textColumn}>
          <Heading id={HERO_BANNER_CONTENT_ID} as="h1" variant="header" size="medium">
            {displayTitle}
          </Heading>
          {showBadge && (
            <div className={titleSectionStyles.badgeRow}>
              <Tag kind={TagKind.Brand} variant={TagVariant.Outline}>
                {residencyTagContent}
              </Tag>
              {!!endDate && (
                <Text variant="label" size="extra-small" color="text.subtle">
                  {untilContent}
                </Text>
              )}
            </div>
          )}
        </div>
      </div>
      <div className={titleSectionStyles.actionsButton}>
        <IconButton
          aria-label={iconButtonAriaLabelContent}
          variant={ButtonVariant.Ghost}
          size={ButtonSize.Small}
          kind={ButtonKind.Neutral}
          shape={ButtonShape.Round}
          icon={MoreIcon}
          onClick={handleDrawerOpen}
        />
      </div>
      <MobileBannerOverflowDrawer
        isOpen={isDrawerOpen}
        handleClose={handleDrawerClose}
        categoryId={categoryId}
        scrollToPerformer={scrollToPerformer}
        categoryName={categoryName ?? ""}
        catHasPerformerCard={!!catHasPerformerCard}
      />
      {!!residencyVenueSubtitle && (
        <Text variant="label" size="small" color="text">
          {residencyVenueSubtitle}
        </Text>
      )}
    </div>
  );
});
TitleSection.displayName = "TitleSection";
